import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CitationsManager = () => {
  const [citations, setCitations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    texte: '',
    categorie: 'chakra',
    employee_name: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterPerson, setFilterPerson] = useState('all'); // Nouveau filtre par personne
  
  // États pour les médias employés
  const [employeeMedias, setEmployeeMedias] = useState([]);
  const [mediaFormData, setMediaFormData] = useState({
    employee_id: '',
    employee_name: '',
    media_type: 'gif',
    filename: '',
    media_data: '',
    active: true,
    start_date: '',
    end_date: ''
  });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [editingMediaId, setEditingMediaId] = useState(null);
  const mediaFileInputRef = useRef(null);
  
  // Liste des personnes/équipes
  const persons = [
    { id: 'all', label: 'Tous', icon: '👥', color: 'bg-gray-100 text-gray-800' },
    { id: 'mmconciergerie', label: 'M&M Conciergerie', icon: '🏢', color: 'bg-blue-100 text-blue-800' },
    { id: 'cyril', label: 'Cyril', icon: '🔥', color: 'bg-red-100 text-red-800' },
    { id: 'ben', label: 'Ben', icon: '💪', color: 'bg-orange-100 text-orange-800' },
    { id: 'marion', label: 'Marion', icon: '👑', color: 'bg-purple-100 text-purple-800' },
    { id: 'magali', label: 'Magali', icon: '✨', color: 'bg-pink-100 text-pink-800' }
  ];

  useEffect(() => {
    loadCitations();
    loadEmployees();
    loadEmployeeMedias();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    }
  };

  const loadEmployeeMedias = async () => {
    try {
      const response = await axios.get(`${API}/employee-medias`);
      setEmployeeMedias(response.data || []);
    } catch (error) {
      console.error('Erreur chargement médias:', error);
    }
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type
    const isGif = file.type === 'image/gif';
    const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
    const isVideo = file.type.startsWith('video/');
    
    if (!isGif && !isImage && !isVideo) {
      toast.error("Format non supporté. Utilisez une image (JPG, PNG, GIF) ou une vidéo (MP4, WebM).");
      return;
    }

    // Vérifier la taille (max 15 Mo)
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Fichier trop volumineux. Maximum 15 Mo.");
      return;
    }

    // Lire le fichier en Base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      let mediaType = 'image';
      if (isGif) mediaType = 'gif';
      else if (isVideo) mediaType = 'video';
      
      setMediaFormData(prev => ({
        ...prev,
        media_type: mediaType,
        filename: file.name,
        media_data: base64
      }));
      setMediaPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleMediaSubmit = async (e) => {
    e.preventDefault();
    
    if (!mediaFormData.employee_id) {
      toast.warning("Veuillez sélectionner un employé");
      return;
    }
    if (!mediaFormData.media_data) {
      toast.warning("Veuillez sélectionner un fichier GIF ou vidéo");
      return;
    }

    setUploadingMedia(true);
    try {
      await axios.post(`${API}/employee-medias`, mediaFormData);
      toast.success("Média ajouté avec succès !");
      
      // Réinitialiser
      setMediaFormData({
        employee_id: '',
        employee_name: '',
        media_type: 'gif',
        filename: '',
        media_data: '',
        active: true,
        start_date: '',
        end_date: ''
      });
      setMediaPreview(null);
      if (mediaFileInputRef.current) {
        mediaFileInputRef.current.value = '';
      }
      
      await loadEmployeeMedias();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de l\'upload du média");
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleToggleMediaActive = async (employeeId, currentActive) => {
    try {
      await axios.put(`${API}/employee-medias/${employeeId}`, {
        active: !currentActive
      });
      await loadEmployeeMedias();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleUpdateMediaDates = async (employeeId, startDate, endDate) => {
    try {
      await axios.put(`${API}/employee-medias/${employeeId}`, {
        start_date: startDate || null,
        end_date: endDate || null
      });
      await loadEmployeeMedias();
      setEditingMediaId(null);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de la mise à jour des dates");
    }
  };

  const handleDeleteMedia = async (employeeId) => {
    try {
      await axios.delete(`${API}/employee-medias/${employeeId}`);
      toast.success("Média supprimé");
      await loadEmployeeMedias();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const loadCitations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/citations`);
      setCitations(response.data);
    } catch (error) {
      console.error('Erreur chargement citations:', error);
      toast.info('Erreur lors du chargement des citations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.texte.trim()) {
      toast.warning("Le texte de la citation est obligatoire");
      return;
    }

    try {
      if (editingId) {
        // Modification
        await axios.put(`${API}/citations/${editingId}`, formData);
        toast.success("Citation modifiée avec succès");
      } else {
        // Création
        await axios.post(`${API}/citations`, formData);
        toast.success("Citation ajoutée avec succès");
      }
      
      // Réinitialiser le formulaire
      setFormData({ texte: '', categorie: 'chakra' });
      setEditingId(null);
      
      // Recharger les citations
      await loadCitations();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (citation) => {
    setFormData({
      texte: citation.texte,
      categorie: citation.categorie
    });
    setEditingId(citation.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (citationId) => {
    // Action directe sans confirmation

    try {
      await axios.delete(`${API}/citations/${citationId}`);
      toast.success("Citation supprimée avec succès");
      await loadCitations();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      texte: '',
      categorie: 'chakra',
      employee_name: ''
    });
    setEditingId(null);
  };

  // Filtrage combiné par catégorie et par personne
  const filteredCitations = citations.filter(citation => {
    // Filtre par catégorie
    const matchesCategorie = filterCategorie === 'all' || citation.categorie === filterCategorie;
    
    // Filtre par personne
    let matchesPerson = true;
    if (filterPerson !== 'all') {
      const employeeName = citation.employee_name ? citation.employee_name.toLowerCase() : '';
      matchesPerson = employeeName.includes(filterPerson.toLowerCase());
    }
    
    return matchesCategorie && matchesPerson;
  });

  const getCategorieIcon = (categorie) => {
    switch(categorie) {
      case 'chakra': return '🔥';
      case 'patronnes': return '👑';
      case 'neutre': return '💬';
      default: return '📝';
    }
  };

  const getCategorieLabel = (categorie) => {
    switch(categorie) {
      case 'chakra': return 'Chakra (Cyril, Ben)';
      case 'patronnes': return 'Patronnes (Marion, Magali)';
      case 'neutre': return 'Neutre';
      default: return categorie;
    }
  };

  const getCategorieColor = (categorie) => {
    switch(categorie) {
      case 'chakra': return 'bg-red-100 text-red-800 border-red-300';
      case 'patronnes': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'neutre': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement des citations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="card">
        <div className="card-header">
          <h1 className="text-2xl font-bold text-gray-900">💬 Gestion des Citations Motivantes</h1>
          <p className="text-sm text-gray-600 mt-1">
            Citations personnalisées pour l'équipe • Rotation quotidienne
          </p>
        </div>
      </div>

      {/* Section Média Employé */}
      <div className="card border-2 border-pink-200 bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">🎬 Média Employé (Photo / GIF / Vidéo)</h2>
          <p className="text-xs text-gray-500 mt-1">
            Ajoutez une photo, un GIF animé ou une vidéo visible sur le lien employé (max 15 Mo)
          </p>
        </div>
        <div className="card-body space-y-4">
          {/* Formulaire d'ajout */}
          <form onSubmit={handleMediaSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sélection employé */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👤 Employé destinataire
                </label>
                <select
                  value={mediaFormData.employee_id}
                  onChange={(e) => {
                    const emp = employees.find(emp => emp.id === e.target.value);
                    setMediaFormData(prev => ({
                      ...prev,
                      employee_id: e.target.value,
                      employee_name: emp ? `${emp.prenom} ${emp.nom || ''}`.trim() : ''
                    }));
                  }}
                  className="form-select w-full"
                  required
                >
                  <option value="">Sélectionner un employé...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.prenom} {emp.nom || ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Upload fichier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📁 Image, GIF ou Vidéo (max 15 Mo)
                </label>
                <input
                  ref={mediaFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  onChange={handleMediaFileChange}
                  className="form-input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Formats : JPG, PNG, GIF, MP4, WebM</p>
              </div>
            </div>

            {/* Prévisualisation */}
            {mediaPreview && (
              <div className="border rounded-lg p-4 bg-white">
                <p className="text-sm text-gray-600 mb-2">📺 Prévisualisation :</p>
                {mediaFormData.media_type === 'video' ? (
                  <video src={mediaPreview} controls className="max-h-60 rounded-lg mx-auto" />
                ) : (
                  <img src={mediaPreview} alt="Preview" className="max-h-60 rounded-lg mx-auto" />
                )}
              </div>
            )}

            {/* Options de programmation pour nouveau média */}
            {mediaFormData.media_data && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-3">📅 Programmation (optionnel)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Date de début</label>
                    <input
                      type="date"
                      value={mediaFormData.start_date}
                      onChange={(e) => setMediaFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="form-input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Date de fin</label>
                    <input
                      type="date"
                      value={mediaFormData.end_date}
                      onChange={(e) => setMediaFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="form-input w-full text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Laissez vide pour afficher en permanence. En dehors des dates, les citations s'afficheront à la place.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={uploadingMedia || !mediaFormData.employee_id || !mediaFormData.media_data}
              className="btn-primary disabled:opacity-50"
            >
              {uploadingMedia ? '⏳ Upload en cours...' : '📤 Ajouter le média'}
            </button>
          </form>

          {/* Liste des médias existants */}
          {employeeMedias.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">📋 Médias attribués :</h3>
              <div className="space-y-3">
                {employeeMedias.map(media => {
                  const isEditing = editingMediaId === media.employee_id;
                  const today = new Date().toISOString().split('T')[0];
                  const isScheduled = media.start_date || media.end_date;
                  const isActiveNow = media.active && 
                    (!media.start_date || today >= media.start_date) && 
                    (!media.end_date || today <= media.end_date);
                  
                  return (
                    <div key={media.id} className={`border rounded-lg p-4 bg-white ${!media.active ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {media.media_type === 'video' ? '🎥' : media.media_type === 'gif' ? '🖼️' : '📷'}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{media.employee_name || 'Employé'}</p>
                            <p className="text-xs text-gray-500">{media.filename}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Toggle actif/inactif */}
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={media.active !== false}
                              onChange={() => handleToggleMediaActive(media.employee_id, media.active !== false)}
                              className="form-checkbox h-5 w-5 text-purple-600 rounded"
                            />
                            <span className="text-sm text-gray-600">Actif</span>
                          </label>
                          <button
                            onClick={() => handleDeleteMedia(media.employee_id)}
                            className="btn-danger text-sm px-3 py-1"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      {/* Statut */}
                      <div className="flex items-center gap-2 mb-2">
                        {isActiveNow ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✅ Affiché maintenant</span>
                        ) : !media.active ? (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">⏸️ Désactivé (citations à la place)</span>
                        ) : (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">📅 Programmé</span>
                        )}
                      </div>
                      
                      {/* Dates de programmation */}
                      {isEditing ? (
                        <div className="border-t pt-3 mt-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Date de début</label>
                              <input
                                type="date"
                                defaultValue={media.start_date || ''}
                                id={`start-${media.employee_id}`}
                                className="form-input w-full text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Date de fin</label>
                              <input
                                type="date"
                                defaultValue={media.end_date || ''}
                                id={`end-${media.employee_id}`}
                                className="form-input w-full text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => {
                                const startInput = document.getElementById(`start-${media.employee_id}`);
                                const endInput = document.getElementById(`end-${media.employee_id}`);
                                handleUpdateMediaDates(media.employee_id, startInput.value, endInput.value);
                              }}
                              className="btn-primary text-sm px-3 py-1"
                            >
                              💾 Enregistrer
                            </button>
                            <button
                              onClick={() => setEditingMediaId(null)}
                              className="btn-secondary text-sm px-3 py-1"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <div>
                            {isScheduled ? (
                              <span>
                                📆 {media.start_date || '...'} → {media.end_date || '...'}
                              </span>
                            ) : (
                              <span className="text-gray-400">Pas de programmation (permanent)</span>
                            )}
                          </div>
                          <button
                            onClick={() => setEditingMediaId(media.employee_id)}
                            className="text-purple-600 hover:text-purple-800 text-sm"
                          >
                            ✏️ Modifier dates
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Boutons de filtrage rapide par personne */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">👥 Filtrer par personne</h2>
          <p className="text-xs text-gray-500 mt-1">Cliquez sur un bouton pour voir les citations de cette personne</p>
        </div>
        <div className="card-body">
          <div className="flex flex-wrap gap-3">
            {persons.map(person => (
              <button
                key={person.id}
                onClick={() => setFilterPerson(person.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                  filterPerson === person.id
                    ? `${person.color} border-current shadow-md scale-105`
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{person.icon}</span>
                {person.label}
                {filterPerson === person.id && (
                  <span className="ml-2 inline-block px-2 py-0.5 bg-white bg-opacity-50 rounded-full text-xs">
                    {filteredCitations.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Formulaire d'ajout/modification */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? '✏️ Modifier la citation' : '➕ Ajouter une nouvelle citation'}
          </h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                👤 Attribuer à une personne
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {persons.filter(p => p.id !== 'all').map(person => (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      employee_name: person.label,
                      categorie: person.id === 'cyril' || person.id === 'ben' ? 'chakra' : 
                                person.id === 'marion' || person.id === 'magali' ? 'patronnes' : 'neutre'
                    })}
                    className={`px-4 py-3 rounded-lg font-medium transition-all border-2 ${
                      formData.employee_name === person.label
                        ? `${person.color} border-current shadow-md`
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-2 text-xl">{person.icon}</span>
                    <span>{person.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Sélectionnez la personne à qui cette citation s'adresse
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte de la citation
              </label>
              <textarea
                value={formData.texte}
                onChange={(e) => setFormData({ ...formData, texte: e.target.value })}
                placeholder="Entrez une citation motivante..."
                rows="4"
                className="form-textarea w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.categorie === 'chakra' && '💪 Citation énergétique et puissante pour booster le chakra'}
                {formData.categorie === 'patronnes' && '✨ Citation valorisante et souriante pour les patronnes'}
                {formData.categorie === 'neutre' && '📝 Citation générale'}
              </p>
            </div>

            <div className="flex space-x-3">
              <button type="submit" className="btn-primary">
                {editingId ? '💾 Enregistrer les modifications' : '➕ Ajouter la citation'}
              </button>
              {editingId && (
                <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                  ❌ Annuler
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Citations Chakra</p>
                <p className="text-2xl font-bold text-red-600">
                  {citations.filter(c => c.categorie === 'chakra').length}
                </p>
              </div>
              <span className="text-4xl">🔥</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Citations Patronnes</p>
                <p className="text-2xl font-bold text-purple-600">
                  {citations.filter(c => c.categorie === 'patronnes').length}
                </p>
              </div>
              <span className="text-4xl">👑</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-blue-600">{citations.length}</p>
              </div>
              <span className="text-4xl">💬</span>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des citations */}
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              📋 Liste des citations
              <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {filteredCitations.length}
              </span>
            </h2>
            {filterPerson !== 'all' && (
              <button
                onClick={() => setFilterPerson('all')}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                ✕ Réinitialiser le filtre
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {filteredCitations.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune citation trouvée</p>
          ) : (
            <div className="space-y-3">
              {filteredCitations.map((citation) => {
                // Trouver la personne attribuée
                const assignedPerson = persons.find(p => 
                  citation.employee_name && citation.employee_name.toLowerCase().includes(p.id.toLowerCase())
                );
                
                return (
                  <div
                    key={citation.id}
                    className={`border-2 rounded-lg p-4 ${getCategorieColor(citation.categorie)} hover:shadow-lg transition-shadow`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xl">{getCategorieIcon(citation.categorie)}</span>
                          <span className="text-xs font-semibold uppercase">
                            {getCategorieLabel(citation.categorie)}
                          </span>
                          {citation.employee_name && assignedPerson && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${assignedPerson.color} border`}>
                              {assignedPerson.icon} {citation.employee_name}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-900 italic leading-relaxed text-base">
                          "{citation.texte}"
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(citation)}
                          className="btn-secondary text-sm px-3 py-1 hover:scale-105 transition-transform"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(citation.id)}
                          className="btn-danger text-sm px-3 py-1 hover:scale-105 transition-transform"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Aide */}
      <div className="card border-2 border-blue-200 bg-blue-50">
        <div className="card-body">
          <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Comment ça marche ?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Les citations sont affichées sur le lien personnel de chaque employé éligible</li>
            <li>• Cyril et Ben voient les citations "Chakra" (énergétiques)</li>
            <li>• Marion et Magali voient les citations "Patronnes" (valorisantes)</li>
            <li>• La citation change automatiquement chaque jour (rotation basée sur la date)</li>
            <li>• Vous pouvez ajouter autant de citations que vous voulez dans chaque catégorie</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CitationsManager;
